import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Globe, RefreshCw, Loader2, Search, User, Mail, Phone,
  UserPlus, CheckCircle, ExternalLink, Trash2, ArrowRight,
  Calendar, AlertCircle, MessageSquare, Clock, FileText, Reply, Facebook,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DuplicateBadge } from '../components/DuplicateBadge';
import { LeadSendEmailDialog } from '../components/LeadSendEmailDialog';
import { BulkEmailLeadsDialog } from '../components/BulkEmailLeadsDialog';

export const WixLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [emailDialogLead, setEmailDialogLead] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [formNameFilter, setFormNameFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [convertDialog, setConvertDialog] = useState(false);
  const [converting, setConverting] = useState(false);
  const [messageDialog, setMessageDialog] = useState(null);
  const [lastSync, setLastSync] = useState('');
  const [stats, setStats] = useState({ total: 0, new: 0, converted: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [duplicates, setDuplicates] = useState({});

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * 50, limit: 50 };
      if (filter === 'new') params.converted = false;
      if (filter === 'converted') params.converted = true;
      if (filter === 'replied') params.replied = true;
      if (filter === 'awaiting') params.replied = false;

      const { data } = await api.get('/wix-leads', { params });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setLastSync(data.last_sync || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load website leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/wix-leads/stats');
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [fetchLeads, fetchStats]);

  useEffect(() => {
    api.get('/duplicates/scan').then(res => {
      setDuplicates(res.data?.wix_leads || {});
    }).catch(() => {});
  }, [leads]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/wix-leads/sync');
      if (data.status === 'running') {
        toast.success(data.message || 'Sync started');
        pollSyncStatus();
      } else {
        toast.success(data.message || 'Sync complete');
        fetchLeads();
        fetchStats();
        setSyncing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
      setSyncing(false);
    }
  };

  const pollSyncStatus = useCallback(() => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get('/wix-leads/sync-status');
        const p = data.progress || {};
        if (data.status === 'running') {
          if (p.fetched) {
            setSyncProgress(`Syncing… ${p.fetched} found, ${p.synced || 0} new`);
          }
        } else {
          clearInterval(interval);
          setSyncing(false);
          setSyncProgress('');
          if (data.status === 'failed') {
            toast.error(data.error || 'Sync failed');
          } else {
            const r = data.result || {};
            toast.success(`Sync complete: ${r.synced ?? 0} new, ${r.skipped ?? 0} already imported`);
          }
          fetchLeads();
          fetchStats();
        }
      } catch {
        // keep polling on transient errors
      }
      // Safety stop after ~5 minutes of polling
      if (attempts > 150) {
        clearInterval(interval);
        setSyncing(false);
        setSyncProgress('');
        fetchLeads();
        fetchStats();
      }
    }, 2000);
  }, [fetchLeads, fetchStats]);

  // Resume progress UI if a sync is already running (e.g. started elsewhere)
  useEffect(() => {
    api.get('/wix-leads/sync-status').then(({ data }) => {
      if (data.status === 'running') {
        setSyncing(true);
        pollSyncStatus();
      }
    }).catch(() => {});
  }, [pollSyncStatus]);

  const handleConvert = async () => {
    if (!selectedLead) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/wix-leads/${selectedLead.id}/convert`);
      toast.success('Lead converted to customer!');
      setConvertDialog(false);
      setSelectedLead(null);
      fetchLeads();
      fetchStats();
      navigate(`/customers/${data.customer_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/wix-leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
      fetchStats();
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const handleToggleReplied = async (lead) => {
    const next = !lead.replied;
    // optimistic update
    setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, replied: next, replied_at: next ? new Date().toISOString() : null } : l));
    try {
      await api.post(`/wix-leads/${lead.id}/replied`, { replied: next });
      toast.success(next ? 'Marked as replied' : 'Marked as not replied');
      fetchStats();
    } catch {
      toast.error('Failed to update reply status');
      fetchLeads();
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredLeads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLeads.map((l) => l.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const label = action === 'delete' ? 'delete' : 'convert';
    if (!confirm(`Are you sure you want to ${label} ${selectedIds.size} lead(s)?`)) return;

    setBulkLoading(true);
    try {
      const { data } = await api.post('/wix-leads/bulk-action', {
        lead_ids: Array.from(selectedIds),
        action,
      });
      toast.success(data.message);
      setSelectedIds(new Set());
      fetchLeads();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Bulk ${label} failed`);
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredLeads = leads.filter((lead) => {
    if (formNameFilter !== 'all') {
      const fn = lead.form_name || 'Contact Form';
      if (fn !== formNameFilter) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(q) ||
      lead.last_name?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q) ||
      lead.phone?.includes(q) ||
      lead.message?.toLowerCase().includes(q) ||
      lead.address?.toLowerCase().includes(q)
    );
  });

  // Distinct form_name list for the filter dropdown
  const distinctFormNames = Array.from(
    new Set(leads.map((l) => l.form_name || 'Contact Form').filter(Boolean))
  ).sort();

  return (
    <Layout>
      <div className="space-y-6" data-testid="wix-leads-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-teal-600 flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Website Leads</h1>
              <p className="text-slate-500 text-sm">
                Contact form submissions from your website
                {lastSync && (
                  <span className="ml-2 text-xs text-slate-400">
                    <Clock className="w-3 h-3 inline mr-0.5" />
                    Last sync: {formatDateTime(lastSync)}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
              <UserPlus className="w-3 h-3 mr-1" />
              {stats.new} New
            </Badge>
            <Badge className="bg-blue-100 text-blue-700 px-3 py-1" data-testid="stat-awaiting-reply">
              <Reply className="w-3 h-3 mr-1" />
              {stats.awaiting_reply ?? 0} Awaiting Reply
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1">
              <CheckCircle className="w-3 h-3 mr-1" />
              {stats.converted} Converted
            </Badge>
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="sync-wix-btn"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {syncing ? (syncProgress || 'Syncing…') : 'Sync Leads'}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, phone, message..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-wix-leads"
                  />
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {['all', 'new', 'awaiting', 'replied', 'converted'].map((f) => (
                    <Button
                      key={f}
                      size="sm"
                      variant={filter === f ? 'default' : 'outline'}
                      onClick={() => { setFilter(f); setPage(0); }}
                      data-testid={`wix-filter-${f}`}
                      className={filter === f ? 'bg-teal-600 hover:bg-teal-700' : ''}
                    >
                      {f === 'all' ? 'All' : f === 'new' ? 'New' : f === 'awaiting' ? 'Awaiting Reply' : f === 'replied' ? 'Replied' : 'Converted'}
                    </Button>
                  ))}
                  <div className="w-48">
                    <Select value={formNameFilter} onValueChange={setFormNameFilter}>
                      <SelectTrigger data-testid="wix-form-name-filter" className="h-9">
                        <SelectValue placeholder="All forms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All forms</SelectItem>
                        {distinctFormNames.map((fn) => (
                          <SelectItem key={fn} value={fn} data-testid={`wix-form-option-${fn}`}>{fn}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="py-3 flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => setBulkEmailOpen(true)}
                    disabled={bulkLoading}
                    data-testid="bulk-email-btn"
                  >
                    <Mail className="w-3 h-3 mr-1" /> Email Selected
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleBulkAction('convert')}
                    disabled={bulkLoading}
                    data-testid="bulk-convert-btn"
                  >
                    {bulkLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />}
                    Convert to Customers
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleBulkAction('delete')}
                    disabled={bulkLoading}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete Selected
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Leads Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="text-center py-12">
                  <Globe className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 mb-2">No website leads found</p>
                  <p className="text-sm text-slate-400 mb-4">
                    {leads.length === 0
                      ? "Click 'Sync Leads' to import submissions from your Wix website"
                      : 'No leads match your search criteria'}
                  </p>
                  {leads.length === 0 && (
                    <Button onClick={handleSync} disabled={syncing} variant="outline">
                      <RefreshCw className="w-4 h-4 mr-2" /> Sync Now
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedIds.size === filteredLeads.length && filteredLeads.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead) => (
                      <TableRow key={lead.id} data-testid={`wix-lead-row-${lead.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(lead.id)}
                            onCheckedChange={() => toggleSelect(lead.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-teal-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {lead.first_name} {lead.last_name}
                              </p>
                              {duplicates[lead.id] && (
                                <DuplicateBadge
                                  matches={duplicates[lead.id]}
                                  currentSource="wix_lead"
                                  currentId={lead.id}
                                  onMerged={fetchLeads}
                                  compact
                                />
                              )}
                              {(duplicates[lead.id] || []).some((m) => m.source === 'meta_lead') && (
                                <a
                                  href="/meta-leads"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-semibold hover:bg-indigo-200 w-fit"
                                  data-testid={`meta-dup-${lead.id}`}
                                  title="This person also submitted a Meta (Facebook/Instagram) lead"
                                >
                                  <Facebook className="w-3 h-3" /> Also in Meta Leads
                                </a>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {lead.email && (
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="w-3 h-3 text-slate-400" />
                                <a href={`mailto:${lead.email}`} className="text-teal-600 hover:underline text-xs">
                                  {lead.email}
                                </a>
                              </div>
                            )}
                            {lead.phone && (
                              <div className="flex items-center gap-1 text-sm text-slate-500">
                                <Phone className="w-3 h-3 text-slate-400" />
                                <span className="text-xs">{lead.phone}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {lead.message ? (
                            <button
                              className="text-left group"
                              onClick={() => setMessageDialog(lead)}
                              data-testid={`read-msg-${lead.id}`}
                            >
                              <p className="text-sm text-slate-600 truncate max-w-[200px] group-hover:text-teal-600 transition-colors">
                                {lead.message}
                              </p>
                              <span className="text-[10px] text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                Click to read full message
                              </span>
                            </button>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            <FileText className="w-3 h-3 mr-1" />
                            {lead.form_name || 'Contact Form'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Calendar className="w-3 h-3" />
                            <span className="text-xs">{formatDate(lead.submitted_at)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {lead.converted ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> Converted
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">New Lead</Badge>
                          )}
                          {lead.replied && (
                            <Badge className="bg-blue-100 text-blue-700 mt-1 flex w-fit items-center" data-testid={`replied-badge-${lead.id}`} title={lead.replied_at ? `Replied ${formatDate(lead.replied_at)}${lead.replied_by ? ` by ${lead.replied_by}` : ''}` : 'Replied'}>
                              <Reply className="w-3 h-3 mr-1" /> Replied
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {lead.email && (
                              lead.email_log?.length > 0 ? (
                                <Button
                                  size="sm"
                                  className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                  onClick={() => setEmailDialogLead(lead)}
                                  data-testid={`email-btn-${lead.id}`}
                                  title={`${lead.email_log.length} email(s) sent — click to send another`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Email Sent{lead.email_log.length > 1 ? ` (${lead.email_log.length})` : ''}
                                </Button>
                              ) : (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-slate-500 hover:text-blue-600"
                                  onClick={() => setEmailDialogLead(lead)}
                                  data-testid={`email-btn-${lead.id}`}
                                  title="Send email"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className={`h-8 w-8 ${lead.replied ? 'text-blue-600 hover:text-blue-700 bg-blue-50' : 'text-slate-500 hover:text-blue-600'}`}
                              onClick={() => handleToggleReplied(lead)}
                              data-testid={`replied-btn-${lead.id}`}
                              title={lead.replied ? 'Replied — click to un-mark' : 'Mark as replied'}
                            >
                              <Reply className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-slate-500 hover:text-teal-600"
                              onClick={() => setMessageDialog(lead)}
                              data-testid={`view-btn-${lead.id}`}
                              title="View details"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            {!lead.converted ? (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                                onClick={() => { setSelectedLead(lead); setConvertDialog(true); }}
                                data-testid={`convert-btn-${lead.id}`}
                              >
                                <UserPlus className="w-3 h-3 mr-1" /> Create Customer
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs"
                                onClick={() => navigate(`/customers/${lead.customer_id}`)}
                                data-testid={`view-customer-btn-${lead.id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" /> View
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(lead.id)}
                              data-testid={`delete-btn-${lead.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {total > 50 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Showing {page * 50 + 1} - {Math.min((page + 1) * 50, total)} of {total} leads
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= total} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Message Popup Dialog */}
        <Dialog open={!!messageDialog} onOpenChange={() => setMessageDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-teal-600" />
                Website Enquiry
              </DialogTitle>
              <DialogDescription>
                Submitted via {messageDialog?.form_name || 'Contact Form'}
              </DialogDescription>
            </DialogHeader>
            {messageDialog && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium">{messageDialog.first_name} {messageDialog.last_name}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {messageDialog.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          <a href={`mailto:${messageDialog.email}`} className="text-teal-600 hover:underline">
                            {messageDialog.email}
                          </a>
                        </span>
                      )}
                      {messageDialog.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {messageDialog.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {messageDialog.address && (
                  <div className="text-sm">
                    <span className="text-slate-500 text-xs font-medium">Address:</span>
                    <p className="text-slate-700">{messageDialog.address}</p>
                  </div>
                )}

                {messageDialog.electric_usage && (
                  <div className="text-sm">
                    <span className="text-slate-500 text-xs font-medium">Electric Usage:</span>
                    <p className="text-slate-700">{messageDialog.electric_usage}</p>
                  </div>
                )}

                <div>
                  <span className="text-slate-500 text-xs font-medium">Message:</span>
                  <ScrollArea className="max-h-60 mt-1">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="lead-message-content">
                        {messageDialog.message || 'No message provided'}
                      </p>
                    </div>
                  </ScrollArea>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Calendar className="w-3 h-3" />
                  Submitted: {formatDateTime(messageDialog.submitted_at)}
                </div>
              </div>
            )}
            <DialogFooter>
              {!messageDialog?.converted && (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    setSelectedLead(messageDialog);
                    setMessageDialog(null);
                    setConvertDialog(true);
                  }}
                  data-testid="dialog-convert-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Create Customer
                </Button>
              )}
              {messageDialog?.converted && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setMessageDialog(null);
                    navigate(`/customers/${messageDialog.customer_id}`);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> View Customer
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert Lead Dialog */}
        <Dialog open={convertDialog} onOpenChange={setConvertDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-600" />
                Convert Lead to Customer
              </DialogTitle>
              <DialogDescription>
                This will create a new customer record from the website lead.
              </DialogDescription>
            </DialogHeader>
            {selectedLead && (
              <div className="py-4 space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-medium text-lg">{selectedLead.first_name} {selectedLead.last_name}</p>
                      <p className="text-sm text-slate-500">Website Lead</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <p className="font-medium">{selectedLead.email || '-'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Phone:</span>
                      <p className="font-medium">{selectedLead.phone || '-'}</p>
                    </div>
                    {selectedLead.address && (
                      <div className="col-span-2">
                        <span className="text-slate-500">Address:</span>
                        <p className="font-medium">{selectedLead.address}</p>
                      </div>
                    )}
                  </div>
                </div>
                {selectedLead.message && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-500 text-xs font-medium">Message (will be saved as notes):</span>
                    <p className="text-sm text-slate-700 mt-1 line-clamp-3">{selectedLead.message}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p>A new customer will be created with status "Lead". The message will be saved in the notes field.</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConvertDialog(false)}>Cancel</Button>
              <Button
                onClick={handleConvert}
                disabled={converting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-convert-btn"
              >
                {converting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Create Customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <LeadSendEmailDialog
        open={!!emailDialogLead}
        onOpenChange={(v) => !v && setEmailDialogLead(null)}
        lead={emailDialogLead}
        leadSource="wix"
      />

      <BulkEmailLeadsDialog
        open={bulkEmailOpen}
        onOpenChange={setBulkEmailOpen}
        selectedLeads={leads
          .filter((l) => selectedIds.has(l.id))
          .map((l) => ({
            id: l.id,
            email: l.email,
            first_name: l.first_name,
            last_name: l.last_name,
            name: `${l.first_name || ''} ${l.last_name || ''}`.trim(),
            phone: l.phone,
          }))}
        leadSource="wix"
        onComplete={fetchLeads}
      />
    </Layout>
  );
};
